import Filters from "./Filters";
import Products from "./Products";
import {ReactNode, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useLocation, useSearchParams} from "react-router-dom";
import {getSuffixByCount} from "../../utils/Utils";
import Pagination from "../core/Pagination";
import {CatalogFilterInterface} from "../../interfaces/catalog/CatalogFilterInterface";
import {useDebounce} from "@uidotdev/usehooks";
import useCatalog from "./useCatalog";
import {RangeFilterInterface} from "../../interfaces/core/RangeFilterInterface";
import {TopItem} from "./TopItem";
import {ApiResponseInterface as CatalogApiResponseInterface} from "../../interfaces/catalog/ApiResponseInterface";
import {formatBoldText} from "../../utils/formatBoldText";
import {DEFAULT_SORT, isSortType, RELEVANCE_SORT, SortType} from "../../utils/productSort";

// headerTarget — заголовок и число товаров не над каталогом, а там (шапка модального окна консультанта)
export default function CatelogBase({response, header, baseUrl, defaultSort = DEFAULT_SORT, categoryBaseUrl, headerPrefix, headerTarget} : {response: CatalogApiResponseInterface, header: string, baseUrl: string, defaultSort?: SortType, categoryBaseUrl?: string, headerPrefix?: ReactNode, headerTarget?: HTMLElement | null}) {

    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    // Страница и «показать еще» — из URL: возврат «назад» восстанавливает их
    const [page, setPage] = useState(() => Number(searchParams.get('page')) || 1);
    const [productsPerPage, ] = useState(36);
    const [additionalPages, setAdditionalPages] = useState(() => Number(searchParams.get('adp')) || 0);
    const [filters, setFilters] = useState<CatalogFilterInterface>({price: {min: 0, max: 0}, filters: {}, brands: []});
    const [productsCount, setProductsCount] = useState(0);
    const debouncedFilters = useDebounce(filters, 500);

    const sortParam = searchParams.get('st');
    const sortType: SortType = isSortType(sortParam) ? sortParam : defaultSort;

    // Пагинация должна сохранять параметры каталога (сортировка, фильтры) —
    // иначе переход на другую страницу терял ?filters= из URL, и возврат «назад»
    // с карточки товара восстанавливал категорию без фильтров
    const paginationQuery = new URLSearchParams();
    if (sortParam) {
        paginationQuery.set('st', sortParam);
    }
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
        paginationQuery.set('filters', filtersParam);
    }

    // Сброс на 1-ю страницу: только по явному действию пользователя (фильтры) или смене данных.
    // Возврат «назад» восстанавливает страницу из URL и сюда не попадает.
    // URL (удаление page/adp) чистит вызывающая сторона — здесь только состояние,
    // чтобы не гоняться с записью URL в Filters одной командой
    const resetPage = () => {
        setPage(1);
        setAdditionalPages(0);
    }

    const changeSort = (newSortType: SortType) => {
        if (newSortType === sortType) return;
        const params = new URLSearchParams(searchParams);
        newSortType === defaultSort ? params.delete('st') : params.set('st', newSortType);
        params.delete('page');
        params.delete('adp');
        setSearchParams(params, {preventScrollReset: true});
        setPage(1);
        setAdditionalPages(0);
    }

    const {
        getFilteredProducts,
        getProductsOnPage,
        getUniqueBrands,
        getPriceFilter
    } = useCatalog(response, filters, productsPerPage, page, additionalPages, sortType);

    const prevProductsRef = useRef<unknown>(undefined);

    useEffect(() => {
        setProductsCount(response?.products?.length || 0);
        const products = response?.products;
        const prev = prevProductsRef.current;
        prevProductsRef.current = products;
        // Первый приход данных — страницу берём из URL (?page=); рефетч без изменений
        // (structural sharing в react-query держит ту же ссылку) сброса не делает
        if (prev === undefined || prev === products) {
            return;
        }
        resetPage();
        if (searchParams.get('page') || searchParams.get('adp')) {
            const params = new URLSearchParams(searchParams);
            params.delete('page');
            params.delete('adp');
            setSearchParams(params, {replace: true, preventScrollReset: true});
        }
    }, [response?.products]);

    useEffect(() => {
        setProductsCount(getFilteredProducts().length || 0);
    }, [debouncedFilters]);

    const totalPages = Math.ceil(productsCount / productsPerPage);

    const toggleFilters = () => {
        setIsFiltersOpen(!isFiltersOpen);
    }

    const count = response?.products && response?.products.length > 0 ? productsCount + ' товар' + getSuffixByCount(productsCount) : null;

    return (<div className="catalog">
        <div className="container">
            {headerTarget ? createPortal(<>
                <span>{headerPrefix}{formatBoldText(header)}</span>
                {count && <span className="text-muted ms-2">{count}</span>}
            </>, headerTarget) : <div className="bs-head catalog__head">
                <h1>{headerPrefix}{formatBoldText(header)}</h1>
                {count && <p>{count}</p>}
            </div>}
            {/* В окне консультанта пустой блок подкатегорий даёт лишний отступ под шапкой */}
            {(!headerTarget || !!response?.subCategories?.length) && <div className="mb-4 category mb-lg-5">
                <div className="gx-3 gx-md-6 row gy-7">
                    {response?.subCategories?.map((category, index) => <TopItem key={index} category={category} basePath={categoryBaseUrl}/>)}
                </div>
            </div>}
            <div
                className={'gx-3 gx-md-6 row gy-7 ' + (totalPages > 1 ? '' : ' mb-5 ') + (isFiltersOpen && ' open-filter')}>
                {response?.products && response?.products?.length > 0 &&<Filters
                    priceFilter={getPriceFilter() as RangeFilterInterface}
                    filters={response?.filters || []}
                    brands={getUniqueBrands() || []}
                    toggleFilters={toggleFilters} setFilters={setFilters}
                    onUserFilterChange={resetPage}
                    productCount={productsCount}
                />}

                {response?.products && response?.products?.length > 0 &&
                    <Products products={getProductsOnPage()} toggleFilters={toggleFilters} sortType={sortType}
                              setSortType={changeSort} withRelevance={defaultSort === RELEVANCE_SORT}/>}
                {totalPages > 1 &&
                    <Pagination page={page} totalPages={totalPages} setPage={setPage} additionalPages={additionalPages}
                                setAdditionalPages={setAdditionalPages}
                                baseUrl={baseUrl}
                                queryString={paginationQuery.toString()}/>}
                {response?.products && response?.products?.length === 0 && <h2 className={'text-center text-bg-light border-1 border-top pt-4'}>Товары не найдены</h2>}
            </div>
        </div>
    </div>)
}